// Oireachtas results pages (MC2 card f157ed22): pure helpers for the list,
// the competition page and the dancer page, which share the feis results
// design (eventResults.css). The data is the published snapshot from
// GET /oireachtas/:id/results[/:compId] and the family / school reads.
import moment from "moment";
import { formatDate } from "../../helper/formatDate";

export const ord = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

// a place as the results books print it: "3rd", or "3T" when tied
export const placeLabel = (position, tied) => (position ? (tied ? `${position}T` : ord(position)) : "");

// Up to two decimals, no trailing zeros (85.5, 60, 62.33).
export const num = (v) => (v == null || v === "" ? "–" : String(Math.round(Number(v) * 100) / 100));

// Type chips on All Results, in Devin's order (2026-10-30): Solo
// championships, Teams, Premiere, Trad sets, Treble reels; then the rest.
const GROUPS = [
  ["Solo championships", ["solo_champ"]],
  ["Teams", ["team_2h", "team_3h", "team_4h", "team_6h", "team_8h"]],
  ["Premiere", ["premiere"]],
  ["Trad sets", ["trad_set"]],
  ["Treble reels", ["treble_reel"]],
  ["Figure and dance drama", ["figure", "dance_drama"]],
  ["Adults", ["adult_solo", "advanced_adult", "adult_team"]],
];
export const groupOf = (type) => (GROUPS.find(([, types]) => types.includes(type)) || ["Other"])[0];
export const groupsOf = (comps) => {
  const present = new Set(comps.map((c) => groupOf(c.type)));
  return [...GROUPS.map(([g]) => g), "Other"].filter((g) => present.has(g));
};

// All Results' list (Devin 2026-10-30): no "All" chip, so a type is always
// chosen (the first present: Solo championships), and a search looks through
// every competition. comps arrive in syllabus order.
export const shownComps = (comps, group, q, matches = (c, t) => compMatches(c, t)) => {
  const groups = groupsOf(comps);
  const grp = groups.includes(group) ? group : groups[0] || null;
  const searching = !!String(q || "").trim();
  const list = comps.filter((c) => (searching || !grp || groupOf(c.type) === grp) && matches(c, q));
  return { groups, grp, searching, list };
};

const fold = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
export const compMatches = (comp, q) => {
  const t = fold(q).trim();
  if (!t) return true;
  return fold(`${comp.code} ${comp.name}`).includes(t);
};
export const includesFolded = (text, q) => fold(text).includes(fold(q).trim());

// "November 6–8, 2026", "October 30 – November 1, 2026", or one day
export const dateLine = (eventDays) => {
  const days = (eventDays || []).map((d) => moment(formatDate(d)));
  if (!days.length) return "";
  const a = days[0];
  const b = days[days.length - 1];
  if (days.length === 1 || a.isSame(b, "day")) return a.format("dddd, MMMM D, YYYY");
  if (a.isSame(b, "month")) return `${a.format("MMMM D")}–${b.format("D, YYYY")}`;
  if (a.isSame(b, "year")) return `${a.format("MMMM D")} – ${b.format("MMMM D, YYYY")}`;
  return `${a.format("MMMM D, YYYY")} – ${b.format("MMMM D, YYYY")}`;
};

// Is today one of the event's days (the visitor's own date)? `days` is the
// server's full list ("YYYY-MM-DD", first to last day filled in by the
// schedule's rule - an event can store only its first and last day).
export const isEventDay = (days, now = moment()) => (days || []).includes(now.format("YYYY-MM-DD"));

// Rounds and their panels, in order: [{ round, judges: [{ slot, name, photo }] }]
export const roundsOf = (detail) =>
  Object.keys(detail?.judgesByRound || {})
    .map(Number)
    .sort((a, b) => a - b)
    .map((round) => ({ round, judges: detail.judgesByRound[round] || [] }));

// One judge, one round (e.g. treble reel, trad set): like the feis single-judge
// table, no Irish points - the judge's score decides (Devin 2026-10-27).
export const isSingleJudge = (detail) => {
  const r = roundsOf(detail);
  return r.length === 1 && r[0].judges.length === 1;
};

export const roundOf = (row, round) => (row?.rounds || []).find((r) => r.round === round) || null;
export const markOf = (row, round, slot) => (roundOf(row, round)?.judges || []).find((j) => j.slot === slot) || null;

// Placed, as the pages show it (Devin 2026-10-27): with a recall, every
// recalled dancer with a place is called up and given a medal, so the recall
// IS the placings; without one, the results' own placed flag.
// A competition has a recall when it ran more than one round and some dancer
// was recalled (MC review: a multi-round competition without a recall must
// not show everyone as "Not recalled").
export const hasRecall = (detail) => roundsOf(detail).length > 1 && (detail?.rows || []).some((r) => r.recalled);

export const placedOf = (row, multiRound) => (multiRound ? !!(row.recalled && row.position) : !!row.placed);

// The table's dividers: placed dancers, then (with a recall) those not
// recalled, or (without one) those who did not place.
// A recalled dancer with no place failed to complete: never "Not recalled".
export const sectionOf = (row, multiRound) => {
  if (placedOf(row, multiRound)) return "placed";
  if (!multiRound) return "unplaced";
  return row.recalled ? "incomplete" : "notRecalled";
};
export const SECTION_LABEL = { unplaced: "Did not place", incomplete: "Did not complete", notRecalled: "Not recalled" };

// Tags after the card number and school (status letters spelled out where
// short enough, qualifications in their book form).
export const tagsOf = (row) => {
  const out = [];
  if ((row.status || []).includes("MW")) out.push({ key: "mw", text: "World medal holder" });
  if ((row.status || []).includes("FTC")) out.push({ key: "ftc", text: "Did not complete" });
  (row.qualifications || []).forEach((q) => out.push({ key: q.key, text: q.short, q: true }));
  return out;
};

// The table's Status column: WM (World medal holder), WQ (World qualifier),
// NQ (NAIDC qualifier), FTC (failed to complete).
export const statusOf = (row) => {
  const out = [];
  // a World medal holder shows WM instead of WQ: one or the other (Devin 2026-10-27)
  if ((row.status || []).includes("MW")) out.push("WM");
  else if ((row.qualifications || []).some((q) => q.key === "wq" || q.key === "wq_auto")) out.push("WQ");
  if ((row.qualifications || []).some((q) => q.key === "naidc")) out.push("NQ");
  if ((row.status || []).includes("FTC")) out.push("FTC");
  return out;
};

// Long tags for a dancer's own page. Round awards are announced on stage
// only, never shown (Devin 2026-10-27).
export const longTagsOf = (row, multiRound) => {
  const out = [];
  if (multiRound && row.recalled) out.push("Recalled");
  if ((row.status || []).includes("MW")) out.push("World medal holder");
  (row.qualifications || []).forEach((q) => out.push(q.long));
  if ((row.status || []).includes("FTC")) out.push("Did not complete");
  return out;
};

// A family / school dancer's row in My Results: what the right-hand pill says.
export const outcomeOf = (r) => {
  if (r.state === "recall_published") return r.recalled ? { kind: "recalled", text: "Recalled" } : { kind: "muted", text: "Not recalled" };
  if (r.ftc) return { kind: "muted", text: "Did not complete" };
  if (placedOf(r, r.recallApplicable) && r.position) return { kind: "place", position: r.position, tied: r.tied };
  if (r.recallApplicable && !r.recalled) return { kind: "muted", text: "Not recalled" };
  return { kind: "muted", text: "Not placed" };
};
